import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { alpha, Typography, useTheme } from '@mui/material';
import type { PageConfig } from 'c-config';
import { PostAuthRoutes } from 'c-routes';
import { taskQueueIcon } from 'c-main/icons';
import type { PopDisplays_JobStatus } from 'c-sdk';
import { PermissionName } from 'c-sdk';
import type {
    FilterableEntityTableProps,
    FilterableEntityTableRefAPI,
    FilterConfig,
    FilterConfigSelect,
    RenderRowProps,
} from 'c-pagination';
import {
    CustomFilterConfigType,
    FilterableEntityTable,
    FilterableEntityTableRendererType,
    generateColumn,
} from 'c-pagination';
import { useEntityData, usePaginatedEntityData } from 'c-data';
import { useDateUtils } from 'c-hooks';
import { getIsDateThisYear } from 'c-lib';
import { FullPageEntityTableWrapper } from 'c-wrapper';
import { parseISO, isValid } from 'date-fns';
import ReactGA from 'react-ga';

import ExpandableHtmlContent from 'c-admin/Pages/TaskQueuePage/ExpandableHtmlContent';

import { useCommonTranslation, useModelFieldTranslation } from 'c-translation';
import StopTaskCol from './StopTaskCol';
import JobStateChip from './JobStateChip';
import FilterToSourceCol from './FilterToSourceCol';
import TaskNameCell from './TaskNameCell';
import DurationCell from './DurationCell';
import { deriveJobState, paginationTag, sourceIdFilterKey, sourceTypeFilterKey } from './lib';

const parseDate = (date: Date | string | null): Date | null => {
    if (date == null) return null;
    const parsed = typeof date === 'string' ? parseISO(date) : date;
    return isValid(parsed) ? parsed : null;
};

type TimeProps = { time?: Date | string | null };

/**
 * No tooltip: the Duration cell beside it already shows Created, Started and Completed in full on
 * hover, so a second one here only offered the same timestamp again a column earlier.
 */
const HumanTimeFormat: React.FC<TimeProps> = ({ time }) => {
    const { formatDateString, timeFormatString } = useDateUtils();
    const date = useMemo(() => parseDate(time), [time]);

    const label = useMemo(() => {
        if (date == null) return null;

        const formattedTime = formatDateString({
            date,
            dateFormatString: '',
            removeTime: false,
            timeFormat: timeFormatString,
        }).trim();

        const now = new Date();
        const isToday =
            date.getDate() === now.getDate() &&
            date.getMonth() === now.getMonth() &&
            date.getFullYear() === now.getFullYear();

        if (isToday) return formattedTime;

        // Without the year, a job from a previous year is indistinguishable from one this year.
        const dateFormat = getIsDateThisYear(date) ? 'dd MMM' : 'dd MMM yyyy';

        return `${formattedTime} (${formatDateString({ date, dateFormatString: dateFormat })})`;
    }, [date, formatDateString, timeFormatString]);

    if (label == null) return null;

    return (
        <Typography align="left" fontSize="small" noWrap>
            {label}
        </Typography>
    );
};

// No id column: the id is an internal handle that means nothing to the reader, and the table is
// still ordered by it (see `orderBy` below), so the rows stay newest-first regardless.
const columns = [
    generateColumn<PopDisplays_JobStatus>({
        headerTitle: 'Modules.PopDisplays.JobStatus.table.statusCol',
        field: 'status',
        entityName: 'PopDisplays_JobStatus',
        RenderCell: ({ entity }) => <JobStateChip entity={entity} />,
    }),
    generateColumn<PopDisplays_JobStatus>({
        headerTitle: 'Modules.PopDisplays.JobStatus.table.nameCol',
        field: 'name',
        entityName: 'PopDisplays_JobStatus',
        RenderCell: TaskNameCell,
        orderable: true,
    }),
    generateColumn<PopDisplays_JobStatus>({
        headerTitle: 'Modules.PopDisplays.JobStatus.table.createdCol',
        field: 'created_at',
        entityName: 'PopDisplays_JobStatus',
        textAlign: 'center',
        orderable: true,
        RenderCell: ({ entity }) => <HumanTimeFormat time={entity.created_at} />,
    }),
    // Started/Updated/Completed are gone — every value on a page fell inside the same minute, and
    // Updated repeated Completed on three rows in four. All four exact timestamps are on the
    // Duration cell's tooltip.
    generateColumn<PopDisplays_JobStatus>({
        headerTitle: 'Modules.PopDisplays.JobStatus.table.durationCol',
        field: 'duration',
        entityName: 'PopDisplays_JobStatus',
        RenderCell: DurationCell,
    }),
    generateColumn<PopDisplays_JobStatus>({
        headerTitle: 'Modules.PopDisplays.JobStatus.table.notesCol',
        field: 'notes',
        entityName: 'PopDisplays_JobStatus',
        RenderCell: ({ entity }) => (
            <Typography
                component="div"
                fontSize="small"
                sx={{ maxWidth: 300, whiteSpace: 'normal', overflowWrap: 'break-word' }}
            >
                <ExpandableHtmlContent contentHtml={entity.notes} />
            </Typography>
        ),
    }),
    generateColumn<PopDisplays_JobStatus>({
        headerTitle: '',
        field: 'actions',
        entityName: 'PopDisplays_JobStatus',
        RenderCell: StopTaskCol,
    }),
    generateColumn<PopDisplays_JobStatus>({
        headerTitle: '',
        field: 'filterToSource',
        entityName: 'PopDisplays_JobStatus',
        textAlign: 'center',
        RenderCell: FilterToSourceCol,
    }),
];

// `notes` is searchable API side, and the API ORs every matched search field together, so passing
// both gives `name LIKE %term% OR notes LIKE %term%`.
const textSearchCols = ['name', 'notes'];

// Only the cheap filters are exposed by the API; keep the rendered set deliberate regardless.
const onlyIncludeFilterKeys = [sourceTypeFilterKey, 'filter.ecs_task_status'];

/**
 * `null` resolves to `completed_at IS NULL OR completed_at = ''`.
 *
 * The opposite can't be written the same way — the API parses `!null` as NOT IN against the
 * literal string 'null' — so "completed" is expressed as "later than a date no job predates".
 * A `>` comparison excludes NULLs in SQL, which is exactly the set we want.
 */
const completionFilter: FilterConfigSelect = {
    key: 'filter.completed_at',
    type: CustomFilterConfigType.Select,
    label: 'Modules.PopDisplays.JobStatus.filters.completion',
    multiple: false,
    options: [
        { label: 'Modules.PopDisplays.JobStatus.filters.notCompleted', value: 'null' },
        {
            label: 'Modules.PopDisplays.JobStatus.filters.completed',
            value: '>1970-01-01 00:00:00',
        },
    ],
};

const refreshIntervalStorageKey = 'taskQueue.refreshInterval';
const MIN_TIMER_VALUE = 1000;
const MAX_TIMER_VALUE = 20000;
const DEFAULT_TIMER_VALUE = 2500;

const clampTimer = (value: unknown): number => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return DEFAULT_TIMER_VALUE;
    return Math.min(MAX_TIMER_VALUE, Math.max(MIN_TIMER_VALUE, parsed));
};

/**
 * There is no UI for this. The interval is still overridable per browser by setting
 * `taskQueue.refreshInterval` in localStorage, which is handy for debugging a busy queue without
 * a deploy; anything unparseable or out of range falls back to the default.
 */
const readStoredTimer = (): number => {
    try {
        const stored = window.localStorage.getItem(refreshIntervalStorageKey);
        return stored == null ? DEFAULT_TIMER_VALUE : clampTimer(stored);
    } catch {
        return DEFAULT_TIMER_VALUE;
    }
};

const TaskQueuePage = () => {
    useEffect(() => {
        ReactGA.pageview(window.location.pathname + window.location.search);
    }, []);

    const t = useCommonTranslation();
    const mt = useModelFieldTranslation();
    const { paginatedData } = usePaginatedEntityData<PopDisplays_JobStatus>(
        paginationTag,
        'PopDisplays_JobStatus',
    );
    const timer = useMemo(readStoredTimer, []);
    const ref = useRef<FilterableEntityTableRefAPI>();

    useEffect(() => {
        const interval = setInterval(() => {
            // Polling also rebuilds the API's filter metadata, so don't do it for a tab nobody's on.
            if (document.visibilityState !== 'visible') return;
            ref?.current?.search?.();
        }, timer);

        return () => clearInterval(interval);
    }, [timer]);

    const { keyedEntities } = useEntityData<PopDisplays_JobStatus>('PopDisplays_JobStatus');
    const theme = useTheme();
    const generateSx = useCallback<RenderRowProps<PopDisplays_JobStatus>['generateSx']>(
        (row, id) => {
            const entity = keyedEntities?.[id];
            if (entity == null) return null;

            const state = deriveJobState(entity);
            // The pastel tints glare on a dark page and wash out its light text, so dark mode uses a
            // translucent wash of each state's colour and keeps the regular text colour.
            if (theme.palette.mode === 'dark') {
                const tint = {
                    succeeded: theme.palette.success.main,
                    failed: theme.palette.error.main,
                    stuck: theme.palette.warning.main,
                }[state];
                return tint ? { bgcolor: alpha(tint, 0.22) } : null;
            }
            if (state === 'succeeded') {
                return { bgcolor: '#90ee90', color: 'success.contrastText' };
            }
            if (state === 'failed') {
                return { bgcolor: '#FF7F7F', color: 'error.contrastText' };
            }
            if (state === 'stuck') {
                return { bgcolor: '#FFD79A', color: 'warning.contrastText' };
            }
            return null;
        },
        [keyedEntities, theme],
    );

    // The source id only means anything alongside a source type — an id on its own would match
    // that number across every kind of record — so the field appears once a type is chosen and
    // names itself after it ("Campaign ID").
    const selectedSourceTypes = useMemo(
        () => (paginatedData?.filters?.[sourceTypeFilterKey] ?? []) as string[],
        [paginatedData?.filters],
    );

    const sourceIdLabel = useMemo(() => {
        if (selectedSourceTypes.length !== 1) {
            return t('Modules.PopDisplays.JobStatus.filters.sourceId');
        }
        return t('Modules.PopDisplays.JobStatus.filters.typedSourceId', {
            type: mt('PopDisplays_JobStatus', 'statusable_type', String(selectedSourceTypes[0])),
        });
    }, [selectedSourceTypes, t, mt]);

    const customFilterConfig = useMemo<FilterConfig[]>(() => {
        const configs: FilterConfig[] = [];

        if (selectedSourceTypes.length > 0) {
            configs.push({
                key: sourceIdFilterKey,
                type: CustomFilterConfigType.Text,
                // Already translated: TextFilter passes this through i18next, which returns an
                // unknown key unchanged.
                label: sourceIdLabel,
                insertAtIndex: 1,
            });
        }
        configs.push(completionFilter);

        return configs;
    }, [selectedSourceTypes.length, sourceIdLabel]);

    // Derived from the payload itself rather than component state, so it can't disagree with the
    // filters actually being sent if a search fires before a re-render settles.
    const updateSearchPayload = useCallback<FilterableEntityTableProps['updateSearchPayload']>(
        payload => {
            const types = payload.filters?.[sourceTypeFilterKey] ?? [];
            if (types.length > 0) return payload;

            // The field is hidden without a source type; don't send a value the user can't see.
            const filters = { ...(payload.filters ?? {}) };
            delete filters[sourceIdFilterKey];

            return { ...payload, filters };
        },
        [],
    );

    return (
        <FullPageEntityTableWrapper title="Modules.PopDisplays.JobStatus.metaTitle">
            <FilterableEntityTable
                ref={ref}
                tag={paginationTag}
                columns={columns}
                revertToIdSearchOnNumberOnlyInput={false}
                baseEntityName="PopDisplays_JobStatus"
                textSearchColumns={textSearchCols}
                generateRowSx={generateSx}
                showFilters
                filtersInlineSearch
                onlyIncludeFilterKeys={onlyIncludeFilterKeys}
                excludeNullFilterOptions
                standaloneResetFilters
                customFilterConfig={customFilterConfig}
                updateSearchPayload={updateSearchPayload}
                tableProps={{
                    boxShadow: 0,
                    sx: { borderCollapse: 'separate' },
                }}
                orderBy="id"
                rendererType={FilterableEntityTableRendererType.Contained}
                dense
                loadBar={false}
            />
        </FullPageEntityTableWrapper>
    );
};

const setup: PageConfig = {
    id: 'TaskQueuePage',
    component: TaskQueuePage,
    disableDesktopContentScrolling: true,
    systemSearch: {
        title: 'Modules.PopDisplays.JobStatus.metaTitle',
        description: 'Modules.PopDisplays.JobStatus.description',
        type: 'page',
        icon: taskQueueIcon,
        aliases: 'SystemSearch.aliases.popDisplaysTaskQueue',
        route: PostAuthRoutes.Admin.TaskQueue,
        saveAsRecentPage: true,
        breadcrumbs: [
            { label: 'Modules.Admin.Home.metaTitle', link: PostAuthRoutes.Admin.AdminHome },
            { label: 'Modules.PopDisplays.JobStatus.metaTitle' },
        ],
        permissions: [PermissionName.Pop_displaysJob_statusesRead],
    },
};

export default setup;
